import { html, unsafeCSS, CSSResultGroup } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import mapNavigationChooserStyles from "../../styles/map-navigation-chooser.css?inline";
import type { LocationDisplayData } from "../../utils/displayedFieldUtils";
import {
  buildLocationSearchQuery,
  getMapNavigationOptions,
  hasValidCoordinates,
  type MapNavigationMode,
} from "../../utils/mapNavigationUtils";
import { t } from "../../utils/widgetsUtils";
import type { WidgetLanguage } from "../../types/widgets.d";
import { ApidaeVisualHost } from "../ApidaeVisualHost";

@customElement("apidae-map-navigation-chooser")
export class MapNavigationChooser extends ApidaeVisualHost {
  static styles: CSSResultGroup = unsafeCSS(mapNavigationChooserStyles);

  @property({ type: String })
  language: WidgetLanguage = "fr" as WidgetLanguage;

  @property({ type: Object })
  location: LocationDisplayData | null = null;

  @property({ type: Array, attribute: false })
  coordinates: [number, number] | null = null;

  @property({ type: Function, attribute: false })
  onClose?: () => void;

  @state()
  private mode: MapNavigationMode = "address";

  private get hasAddress(): boolean {
    return !!this.location && !!buildLocationSearchQuery(this.location);
  }

  private get hasCoords(): boolean {
    return hasValidCoordinates(this.coordinates);
  }

  private get showModeToggle(): boolean {
    return this.hasAddress && this.hasCoords;
  }

  updated(changedProperties: import("lit").PropertyValues) {
    super.updated(changedProperties);
    if (
      changedProperties.has("location") ||
      changedProperties.has("coordinates")
    ) {
      this.mode = this.hasAddress ? "address" : "coordinates";
    }
  }

  private handleBackdropClick(e: Event): void {
    if (e.target === e.currentTarget) {
      this.onClose?.();
    }
  }

  private handleOptionClick(): void {
    this.onClose?.();
  }

  render() {
    if (!this.location) {
      return html``;
    }

    const query = buildLocationSearchQuery(this.location);
    const options = getMapNavigationOptions({
      query,
      coordinates: this.coordinates,
      label: this.location.nomDuLieu || query,
      mode: this.mode,
    });

    if (options.length === 0) {
      return html``;
    }

    return html`
      <div
        class="map-navigation-chooser"
        @click=${this.handleBackdropClick}
        role="presentation"
      >
        <div
          class="map-navigation-chooser__panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="map-navigation-chooser-title"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <h2
            id="map-navigation-chooser-title"
            class="map-navigation-chooser__title"
          >
            ${t("openAddressIn", this.language)}
          </h2>
          ${this.showModeToggle
            ? html`
                <div class="map-navigation-chooser__toggle">
                  <button
                    type="button"
                    class="map-navigation-chooser__toggle-option ${this.mode ===
                    "address"
                      ? "map-navigation-chooser__toggle-option--active"
                      : ""}"
                    @click=${() => {
                      this.mode = "address";
                    }}
                    aria-pressed=${this.mode === "address" ? "true" : "false"}
                  >
                    ${t("address", this.language)}
                  </button>
                  <button
                    type="button"
                    class="map-navigation-chooser__toggle-option ${this.mode ===
                    "coordinates"
                      ? "map-navigation-chooser__toggle-option--active"
                      : ""}"
                    @click=${() => {
                      this.mode = "coordinates";
                    }}
                    aria-pressed=${this.mode === "coordinates"
                      ? "true"
                      : "false"}
                  >
                    ${t("coordinates", this.language)}
                  </button>
                </div>
              `
            : ""}
          <ul class="map-navigation-chooser__options">
            ${options.map(
              (option) => html`
                <li class="map-navigation-chooser__option">
                  <a
                    class="map-navigation-chooser__option-link"
                    href=${option.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    @click=${this.handleOptionClick}
                  >
                    ${t(option.labelKey, this.language)}
                  </a>
                </li>
              `,
            )}
          </ul>
          <button
            type="button"
            class="map-navigation-chooser__cancel"
            @click=${() => this.onClose?.()}
          >
            ${t("cancel", this.language)}
          </button>
        </div>
      </div>
    `;
  }
}
